import { type FC, Fragment } from 'react';

import { AvailableCurrencies, CURRENCY_SYMBOLS, formatCost } from 'utils/clickme';
import { DOT, MINUS_SIGN, NON_BREAKING_SPACE, PLUS_SIGN } from 'utils/constants/common';
import { formatDate } from 'utils/date';

import balanceAndBonusesHistoryFileDownloadButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/balance_and_bonuses/balance_and_bonuses_history_file_download_button_click';
import { Cell, CellText, Link, Text, VSpacingContainer } from '@hh.ru/magritte-ui';
import { DocumentOutlinedSize24, GiftOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import capitalize from 'bloko/common/capitalize';

import useTrls from 'hooks/translation/useTrls';

import { PaymentCancelIcon } from 'components/BalanceAndBonuses/History/components/PaymentCancelIcon';
import { PaymentNewIcon } from 'components/BalanceAndBonuses/History/components/PaymentNewIcon';

import {
    BALANCE_HISTORY_CONFIG,
    type BalanceHistory,
    BalanceHistoryTransactionType,
    BalanceHistoryType,
    isHistoryBonusItem,
} from 'store/models/balance';

import styles from 'components/BalanceAndBonuses/History/History.module.less';

const AMOUNT_SIGN = {
    [BalanceHistoryTransactionType.Profit]: PLUS_SIGN,
    [BalanceHistoryTransactionType.Expense]: MINUS_SIGN,
    [BalanceHistoryTransactionType.Flat]: '',
};
const AMOUNT_STYLE: Partial<Record<BalanceHistoryType, 'positive' | 'warning' | 'negative'>> = {
    [BalanceHistoryType.PaymentNew]: 'warning' as const,
    [BalanceHistoryType.PaymentPayed]: 'positive' as const,
    [BalanceHistoryType.PaymentCancel]: 'negative' as const,
    [BalanceHistoryType.BonusExpire]: 'negative' as const,
    [BalanceHistoryType.BonusRefill]: 'positive' as const,
};

const RECORDS_WITH_FILES = [
    BalanceHistoryType.PaymentNew,
    BalanceHistoryType.PaymentPayed,
    BalanceHistoryType.PaymentCancel,
];

export const HistoryRecords: FC<{ historyRecords: Array<BalanceHistory> }> = ({ historyRecords }) => {
    const trls = useTrls();

    return (
        <VSpacingContainer default={20}>
            {historyRecords.map((monthRecords, i) => {
                const monthTrl = formatDate(monthRecords[0].dateEvent, 'LLLL');

                return (
                    <VSpacingContainer key={i} default={8}>
                        <Text style="secondary" typography="label-2-regular" data-qa="history_month">
                            {capitalize(monthTrl)}
                        </Text>
                        <VSpacingContainer default={0}>
                            {monthRecords.map((record, index) => {
                                const isBonus = isHistoryBonusItem(record);

                                return (
                                    <Cell
                                        key={index}
                                        vertPadding
                                        showDivider
                                        left={
                                            isBonus ? (
                                                <GiftOutlinedSize24
                                                    initialColor="dreamy"
                                                    borderRadius={12}
                                                    padding={12}
                                                    backgroundStyle="dreamy-secondary"
                                                />
                                            ) : (
                                                <DocumentOutlinedSize24
                                                    initialColor="secondary"
                                                    borderRadius={12}
                                                    padding={12}
                                                    backgroundStyle="secondary"
                                                />
                                            )
                                        }
                                        right={
                                            <div className={styles.recordRight}>
                                                <div className={styles.recordCost}>
                                                    <PaymentNewIcon balanceHistoryType={record.type} />
                                                    <PaymentCancelIcon balanceHistoryType={record.type} />
                                                    <Text
                                                        style={AMOUNT_STYLE[record.type]}
                                                        typography="label-2-regular"
                                                        data-qa={record.type}
                                                    >
                                                        {
                                                            AMOUNT_SIGN[
                                                                BALANCE_HISTORY_CONFIG[record.type].transactionType
                                                            ]
                                                        }
                                                        {formatCost(record.sum, NON_BREAKING_SPACE)}
                                                        {!isBonus && (
                                                            <>
                                                                {NON_BREAKING_SPACE}
                                                                {CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}
                                                            </>
                                                        )}
                                                    </Text>
                                                </div>
                                                <Text style="secondary" typography="label-3-regular">
                                                    {formatDate(record.dateEvent, 'd MMMM')}
                                                </Text>
                                            </div>
                                        }
                                    >
                                        <CellText>
                                            {trls(`BalanceAndBonuses.History.operations.${record.type}`, [monthTrl])}
                                        </CellText>
                                        {RECORDS_WITH_FILES.includes(record.type) && !!record.files?.length && (
                                            <CellText type="subtitle">
                                                <div className={styles.recordFiles}>
                                                    {record.files.map((file, fileIndex) => (
                                                        <Fragment key={file.url}>
                                                            {!!fileIndex && (
                                                                <Text style="accent" typography="label-3-regular">
                                                                    {DOT}
                                                                </Text>
                                                            )}
                                                            <Link
                                                                inline
                                                                href={file.url}
                                                                typography="label-3-regular"
                                                                download
                                                                onClick={() => {
                                                                    balanceAndBonusesHistoryFileDownloadButtonClick({
                                                                        fileType: file.type,
                                                                    });
                                                                }}
                                                            >
                                                                {trls(
                                                                    `BalanceAndBonuses.History.HistoryRecords.${file.type}`
                                                                )}
                                                            </Link>
                                                        </Fragment>
                                                    ))}
                                                </div>
                                            </CellText>
                                        )}
                                    </Cell>
                                );
                            })}
                        </VSpacingContainer>
                    </VSpacingContainer>
                );
            })}
        </VSpacingContainer>
    );
};
